import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Play, Pause, Music, BookOpen, Loader2, SkipForward,
  SkipBack, Volume2, VolumeX, ChevronDown, ChevronUp, Mic, Star,
  Flame, Trophy, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChristianWorshipSongs = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const lyricsRef = useRef(null);

  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSong, setCurrentSong] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [singAlongMode, setSingAlongMode] = useState(false);
  const [currentLyricLine, setCurrentLyricLine] = useState(-1);
  const [filter, setFilter] = useState('all');

  // Session tracking
  const [songsListened, setSongsListened] = useState(new Set());
  const [sessionStatus, setSessionStatus] = useState(null);
  const [sessionComplete, setSessionComplete] = useState(false);

  useEffect(() => {
    fetchSongs();
    fetchSessionStatus();
  }, []);

  const fetchSongs = async () => {
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/christian/worship-songs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      setSongs(data.songs || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchSessionStatus = async () => {
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/christian/worship-session/status/${childId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      setSessionStatus(data);
      if (data.completed_today) setSessionComplete(true);
    } catch {}
  };

  const completeSession = async () => {
    if (songsListened.size < 3) return;
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/christian/worship-session/complete?child_id=${childId}&songs_listened=${songsListened.size}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!data.already_completed) {
        toast.success(`Worship session complete! +${data.xp_earned} XP`);
        setSessionComplete(true);
        setSessionStatus(prev => ({ ...prev, completed_today: true, streak: data.streak }));
      }
    } catch (e) { console.error(e); }
  };

  const playSong = async (song, index) => {
    if (currentSong?.id === song.id && isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }
    setCurrentSong(song);
    setCurrentIndex(index);
    setIsPlaying(false);
    setCurrentLyricLine(-1);
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/christian/worship-songs/${song.id}/stream`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (e) { console.error(e); }
  };

  const getFilteredSongs = () => {
    if (filter === 'all') return songs;
    if (filter === 'general') return songs.filter(s => s.is_general);
    return songs.filter(s => s.week_number === parseInt(filter));
  };

  const playNext = () => {
    const f = getFilteredSongs();
    if (currentIndex < f.length - 1) playSong(f[currentIndex + 1], currentIndex + 1);
  };
  const playPrev = () => {
    const f = getFilteredSongs();
    if (currentIndex > 0) playSong(f[currentIndex - 1], currentIndex - 1);
  };
  const toggleMute = () => {
    if (audioRef.current) { audioRef.current.muted = !muted; setMuted(!muted); }
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setProgress(audioRef.current.currentTime);
    setDuration(audioRef.current.duration || 0);

    // Sing-along: update current lyric line
    if (singAlongMode && currentSong?.lyrics_timestamps) {
      const t = audioRef.current.currentTime;
      const idx = currentSong.lyrics_timestamps.findIndex((l, i) => t >= l.start && t < l.end);
      if (idx !== currentLyricLine) {
        setCurrentLyricLine(idx);
        // Auto-scroll
        if (lyricsRef.current && idx >= 0) {
          const el = lyricsRef.current.children[idx];
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  };

  const handleSongEnd = () => {
    // Track song listened
    if (currentSong) {
      setSongsListened(prev => {
        const next = new Set(prev);
        next.add(currentSong.id);
        // Auto-complete session at 3 songs
        if (next.size >= 3 && !sessionComplete) {
          setTimeout(() => completeSession(), 500);
        }
        return next;
      });
    }
    playNext();
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    if (audioRef.current && duration) audioRef.current.currentTime = pct * duration;
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };

  const filtered = getFilteredSongs();
  const weeks = [...new Set(songs.filter(s => s.week_number).map(s => s.week_number))].sort((a, b) => a - b);
  const hasTimestamps = currentSong?.lyrics_timestamps?.length > 0;
  const hasLyrics = currentSong?.lyrics?.trim();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-slate-900">
      <audio ref={audioRef} onTimeUpdate={handleTimeUpdate}
        onEnded={handleSongEnd} onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} />

      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-purple-500/20 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" className="rounded-full text-white"
            onClick={() => navigate(-1)} data-testid="songs-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-white flex items-center gap-2">
              <Music className="w-5 h-5 text-purple-400" /> Worship Songs
            </h1>
            <p className="text-xs text-purple-300">{songs.length} songs</p>
          </div>
          {/* Session progress */}
          <div className="flex items-center gap-2">
            {!sessionComplete && songsListened.size > 0 && (
              <div className="flex items-center gap-1 bg-purple-500/20 rounded-full px-3 py-1">
                <Music className="w-3 h-3 text-purple-400" />
                <span className="text-xs font-bold text-purple-300">{songsListened.size}/3</span>
              </div>
            )}
            {sessionComplete && (
              <div className="flex items-center gap-1 bg-emerald-500/20 rounded-full px-3 py-1" data-testid="session-complete-badge">
                <Check className="w-3 h-3 text-emerald-400" />
                <span className="text-xs font-bold text-emerald-300">Session Done</span>
              </div>
            )}
            {sessionStatus?.streak > 0 && (
              <div className="flex items-center gap-1 bg-orange-500/20 rounded-full px-2 py-1">
                <Flame className="w-3 h-3 text-orange-400" />
                <span className="text-xs font-bold text-orange-300">{sessionStatus.streak}</span>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-4">
        {/* Session XP banner */}
        {!sessionComplete && (
          <Card className="bg-gradient-to-r from-purple-500/20 to-fuchsia-500/20 border-purple-500/30 mb-4" data-testid="session-banner">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/30 rounded-xl flex items-center justify-center">
                <Star className="w-5 h-5 text-purple-400" />
              </div>
              <div className="flex-1">
                <p className="text-purple-200 text-sm font-bold">Worship Session</p>
                <p className="text-purple-400 text-xs">Listen to 3 songs to earn XP!</p>
              </div>
              <div className="flex gap-1">
                {[0, 1, 2].map(i => (
                  <div key={i} className={`w-3 h-3 rounded-full ${i < songsListened.size ? 'bg-purple-400' : 'bg-slate-700'}`} />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          <button onClick={() => setFilter('all')} data-testid="filter-all"
            className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
              filter === 'all' ? 'bg-purple-500 text-white' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>
            All Songs
          </button>
          <button onClick={() => setFilter('general')} data-testid="filter-general"
            className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
              filter === 'general' ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>
            General
          </button>
          {weeks.map(w => (
            <button key={w} onClick={() => setFilter(w.toString())}
              className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
                filter === w.toString() ? 'bg-blue-500 text-white' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>
              Week {w}
            </button>
          ))}
        </div>

        {/* Sing-Along View */}
        <AnimatePresence>
          {singAlongMode && currentSong && hasLyrics && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              className="mb-4">
              <Card className="bg-slate-800/80 border-purple-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-purple-300 text-sm font-bold flex items-center gap-2">
                      <Mic className="w-4 h-4" /> Sing Along
                    </p>
                    <button onClick={() => setSingAlongMode(false)} className="text-slate-500 text-xs">Close</button>
                  </div>
                  <div ref={lyricsRef} className="max-h-48 overflow-y-auto space-y-2 scroll-smooth" data-testid="singalong-lyrics">
                    {hasTimestamps ? (
                      currentSong.lyrics_timestamps.map((line, i) => (
                        <p key={i}
                          className={`text-center transition-all duration-300 px-2 py-1 rounded-lg ${
                            i === currentLyricLine
                              ? 'text-white text-lg font-bold bg-purple-500/20 scale-105'
                              : i < currentLyricLine
                              ? 'text-slate-500 text-sm'
                              : 'text-slate-400 text-sm'
                          }`}>
                          {line.line}
                        </p>
                      ))
                    ) : (
                      // Show plain lyrics if no timestamps
                      currentSong.lyrics.split('\n').map((line, i) => (
                        <p key={i} className="text-slate-300 text-center text-sm">{line || '\u00A0'}</p>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Song List */}
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <Music className="w-12 h-12 mx-auto text-slate-600 mb-3" />
            <p className="text-slate-400 font-medium">No songs yet</p>
            <p className="text-sm text-slate-500">Your teacher will add worship songs soon!</p>
          </div>
        ) : (
          <div className="space-y-2 mb-32">
            {filtered.map((song, i) => (
              <motion.div key={song.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}>
                <Card className={`cursor-pointer transition-all border ${
                  currentSong?.id === song.id
                    ? 'bg-purple-500/20 border-purple-500/40'
                    : 'bg-slate-800/40 border-slate-700/50 hover:border-purple-500/30'
                }`} onClick={() => playSong(song, i)} data-testid={`song-item-${i}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        currentSong?.id === song.id && isPlaying ? 'bg-purple-500 animate-pulse' : 'bg-slate-700'
                      }`}>
                        {currentSong?.id === song.id && isPlaying
                          ? <Pause className="w-4 h-4 text-white" />
                          : <Play className="w-4 h-4 text-purple-300 ml-0.5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold truncate ${
                          currentSong?.id === song.id ? 'text-purple-300' : 'text-white'}`}>{song.title}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                          {song.artist && <span>{song.artist}</span>}
                          {song.bible_verse && <span className="flex items-center gap-0.5"><BookOpen className="w-3 h-3" /> {song.bible_verse}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {songsListened.has(song.id) && <Check className="w-4 h-4 text-emerald-400" />}
                        {song.lyrics_timestamps?.length > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">Sing</span>
                        )}
                        {song.week_number > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">Wk {song.week_number}</span>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Now Playing Bar */}
      <AnimatePresence>
        {currentSong && (
          <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }}
            className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-md border-t border-purple-500/30 z-50"
            data-testid="now-playing-bar">
            <div className="h-1 bg-slate-800 cursor-pointer" onClick={handleSeek}>
              <div className="h-full bg-gradient-to-r from-purple-500 to-fuchsia-500 transition-all"
                style={{ width: duration > 0 ? `${(progress / duration) * 100}%` : '0%' }} />
            </div>
            <div className="max-w-2xl mx-auto px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{currentSong.title}</p>
                  <p className="text-purple-300 text-xs truncate">{currentSong.artist || 'Unknown'}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-slate-500 font-mono">{formatTime(progress)}</span>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-white" onClick={playPrev}
                    disabled={currentIndex <= 0} data-testid="prev-btn">
                    <SkipBack className="w-4 h-4" />
                  </Button>
                  <Button size="icon" className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 text-white"
                    onClick={() => { if (isPlaying) audioRef.current?.pause(); else audioRef.current?.play(); }}
                    data-testid="play-pause-btn">
                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-white" onClick={playNext}
                    disabled={currentIndex >= filtered.length - 1} data-testid="next-btn">
                    <SkipForward className="w-4 h-4" />
                  </Button>
                  <span className="text-[10px] text-slate-500 font-mono">{formatTime(duration)}</span>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-white" onClick={toggleMute}>
                    {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </Button>
                  {/* Sing-along toggle */}
                  {hasLyrics && (
                    <Button variant="ghost" size="icon"
                      className={`h-8 w-8 ${singAlongMode ? 'text-purple-400' : 'text-slate-500'}`}
                      onClick={() => setSingAlongMode(!singAlongMode)}
                      data-testid="singalong-btn">
                      <Mic className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChristianWorshipSongs;
